import { ChartFrame } from '../components/charts/ChartFrame';
import { TrendChart } from '../components/charts/TrendChart';
import { Heatmap, type HeatCell } from '../components/charts/Heatmap';
import { StatTile } from '../components/tiles/StatTile';
import { compact, duration, full, longDate, percent, shortDate, WEEKDAYS } from '../lib/format';
import { sumFor } from '../data/types';
import type { PlatformFilter, Snapshot } from '../data/types';

interface Props {
  snap: Snapshot;
  filter: PlatformFilter;
}

const RETENTION_OFFSETS = [0, 1, 3, 7, 14, 21, 30];

export function Engagement({ snap, filter }: Props) {
  const { dau, wau, mau } = snap.activeUsers;

  const stickiness = dau.map((d, i) => {
    const m = sumFor(mau[i] ?? mau[mau.length - 1], filter) || 1;
    return { date: d.date, stickiness: (sumFor(d, filter) / m) * 100 };
  });

  const actives = dau.map((d, i) => ({
    date: d.date,
    dau: sumFor(d, filter),
    wau: sumFor(wau[i] ?? wau[wau.length - 1], filter),
    mau: sumFor(mau[i] ?? mau[mau.length - 1], filter),
  }));

  /* Retention cohorts — one sequential ramp, nulls left empty. */
  const retentionCells: HeatCell[] = [];
  snap.retention.forEach((cohort, r) => {
    cohort.days.forEach((v, c) => {
      retentionCells.push({ row: r, col: c, value: v === null ? null : v * 100 });
    });
  });

  /* Hour × weekday grid. */
  const hourCells: HeatCell[] = snap.engagement.hourly.map((h) => ({
    row: h.weekday,
    col: h.hour,
    value: h.sessions,
  }));

  const day7 = snap.retention
    .map((c) => c.days[3])
    .filter((v): v is number => v !== null);
  const day30 = snap.retention
    .map((c) => c.days[6])
    .filter((v): v is number => v !== null);
  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

  return (
    <>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
        <StatTile
          label="Stickiness"
          value={percent(snap.summary.stickiness.value)}
          delta={snap.summary.stickiness.delta}
          history={snap.summary.stickiness.history}
          foot="DAU ÷ MAU"
        />
        <StatTile
          label="Day-7 retention"
          value={percent(mean(day7))}
          history={day7.map((v) => v * 100)}
          foot={`mean of ${day7.length} cohorts`}
        />
        <StatTile
          label="Day-30 retention"
          value={percent(mean(day30))}
          history={day30.map((v) => v * 100)}
          foot={`mean of ${day30.length} cohorts`}
        />
        <StatTile
          label="Avg session length"
          value={duration(snap.summary.avgSessionSec.value)}
          delta={snap.summary.avgSessionSec.delta}
          history={snap.summary.avgSessionSec.history}
        />
      </div>

      <div className="grid">
        <div data-span style={{ gridColumn: 'span 7' }}>
          <ChartFrame
            title="Active users — daily, weekly, monthly"
            subtitle="Three windows on one axis"
            table={{
              columns: ['Date', 'DAU', 'WAU', 'MAU'],
              rows: actives.map((a) => [longDate(a.date), full(a.dau), full(a.wau), full(a.mau)]),
            }}
          >
            <TrendChart
              data={actives}
              series={[
                { key: 'dau', label: 'Daily', color: 'var(--series-1)' },
                { key: 'wau', label: 'Weekly', color: 'var(--series-3)' },
                { key: 'mau', label: 'Monthly', color: 'var(--series-7)' },
              ]}
              height={272}
            />
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 5' }}>
          <ChartFrame
            title="Stickiness"
            subtitle="Share of monthly users who return on a given day"
            table={{
              columns: ['Date', 'Stickiness'],
              rows: stickiness.map((s) => [longDate(s.date), `${s.stickiness.toFixed(1)}%`]),
            }}
          >
            <TrendChart
              data={stickiness}
              series={[{ key: 'stickiness', label: 'Stickiness', color: 'var(--series-1)' }]}
              variant="area"
              height={272}
              valueFormat={(n) => `${n.toFixed(0)}%`}
            />
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 12' }}>
          <ChartFrame
            title="Retention by cohort"
            subtitle="Share of each weekly install cohort still active N days later"
            table={{
              columns: ['Cohort', 'Size', ...RETENTION_OFFSETS.map((d) => `D${d}`)],
              rows: snap.retention.map((c) => [
                shortDate(c.cohort),
                full(c.size),
                ...c.days.map((v) => (v === null ? '—' : percent(v, 0))),
              ]),
            }}
          >
            <Heatmap
              cells={retentionCells}
              rowLabels={snap.retention.map((c) => shortDate(c.cohort))}
              colLabels={RETENTION_OFFSETS.map((d) => `D${d}`)}
              formatValue={(v) => `${v.toFixed(0)}%`}
              formatTip={(r, c, v) =>
                `${shortDate(snap.retention[r].cohort)} cohort (${full(snap.retention[r].size)} users) — ${v.toFixed(1)}% still active on day ${RETENTION_OFFSETS[c]}`
              }
              cellHeight={34}
              min={0}
              max={100}
              legendLabel="Hover or tap a cell for the exact cohort figure"
            />
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 12' }}>
          <ChartFrame
            title="When people browse"
            subtitle="Sessions by local hour and weekday"
            table={{
              columns: ['Weekday', ...Array.from({ length: 24 }, (_, h) => `${h}h`)],
              rows: WEEKDAYS.map((d, r) => [
                d,
                ...Array.from({ length: 24 }, (_, h) => {
                  const cell = snap.engagement.hourly.find((x) => x.weekday === r && x.hour === h);
                  return compact(cell?.sessions ?? 0);
                }),
              ]),
            }}
          >
            <Heatmap
              cells={hourCells}
              rowLabels={WEEKDAYS}
              colLabels={Array.from({ length: 24 }, (_, h) => `${h}`)}
              colLabelEvery={2}
              formatValue={compact}
              formatTip={(r, c, v) => `${WEEKDAYS[r]} at ${String(c).padStart(2, '0')}:00 — ${full(v)} sessions`}
              cellHeight={26}
              legendLabel="Hover or tap a cell for the exact session count"
            />
          </ChartFrame>
        </div>
      </div>
    </>
  );
}
